/**
 * Générateur — Attendu 653 — Distance entre deux points
 * Niveau : 6ème | Cluster : 29 | Domaine : Espace et géométrie
 *
 * 6 exercices / 6 formats :
 *   - notation_vf_tableau          : V/F sur distance AB vs segment [AB]
 *   - mesurer_regle_saisie         : mesurer un segment à la règle graduée (SVG)
 *   - comparer_compas_tableau      : comparer des longueurs au compas (<, =, >) (SVG)
 *   - perimetre_compas_qcm_unique  : comparer deux périmètres au compas (SVG)
 *   - position_point_qcm_unique    : distance selon la position du point (3 cas)
 *   - alignement_qcm_unique        : alignement par inégalité triangulaire
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_653.md et docs/fiches/PAYLOADS_653.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 653;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NOTATION = 'Notation distance/segment (Vrai/Faux)';
const L_MESURER = 'Mesurer une distance à la règle graduée';
const L_COMPARER = 'Comparer au compas';
const L_PERIMETRE = 'Périmètres au compas';
const L_POSITION = 'Distance selon la position du point';
const L_ALIGNEMENT = 'Alignement (inégalité triangulaire)';

// Lettres valides pour nommer les points (on exclut I et O : ambiguïté avec 1 et 0).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Formate un nombre à la française (virgule décimale). */
const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    return String(n).replace('.', ',');
};

/** Tire n lettres distinctes dans LETTRES. */
const pickN = (n) => pickLettresSains(LETTRES, n);

/** Construit un élément image (SVG base64) pour `elements_additionnels`. */
const imageElement = (svg, fileName) => ({
    type: 'image',
    config: {
        imageData: `data:image/svg+xml;base64,${svgToBase64(svg)}`,
        fileName,
    },
});

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Notation V/F (tableau vraiFaux)
// ═══════════════════════════════════════════════════════════

const POOL_VRAIES = (A, B, n) => [
    `[${A}${B}] est un segment`,
    `(${A}${B}) est une droite`,
    `${A}${B} = ${n} cm est une notation correcte`,
    `${A}${B} désigne la longueur du segment [${A}${B}]`,
    `${A}${B} = ${B}${A}`,
    `La notation ${A}${B} représente une distance`,
];

const POOL_FAUSSES = (A, B, n) => [
    `[${A}${B}] = ${n} cm est une notation correcte`,
    `${A}${B} est un segment`,
    `(${A}${B}) = ${n} cm est une notation correcte`,
    `${A}${B} et [${A}${B}] désignent la même chose`,
    `[${A}${B}] désigne la longueur du segment`,
    `On peut tracer la distance ${A}${B} au crayon`,
    `(${A}${B}) est un segment`,
];

const genNotationVF = () => {
    const [A, B] = pickN(2);
    const n = randInt(2, 9);

    const vraies = POOL_VRAIES(A, B, n);
    const fausses = POOL_FAUSSES(A, B, n);

    // 3 vraies + 3 fausses pour chaque exercice (6 lignes, bon équilibre).
    const chosenVraies = randomChoiceMultiple(vraies, 3);
    const chosenFausses = randomChoiceMultiple(fausses, 3);

    const merged = shuffle([
        ...chosenVraies.map(l => ({ ligne: l, reponse: 0 })), // 0 = Vrai
        ...chosenFausses.map(l => ({ ligne: l, reponse: 1 })), // 1 = Faux
    ]);

    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    // Consigne volontairement minimale : les colonnes Vrai/Faux disent déjà quoi
    // faire. On garde juste de quoi poser les deux points avant le tableau.
    const texte = `<p>${A} et ${B} sont deux points.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_NOTATION));
    payload._meta.generatorKey = 'notation_vf_tableau';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Mesurer à la règle graduée (saisie + SVG)
// ═══════════════════════════════════════════════════════════

const buildRegleSvg = (A, B, longueurCm) => {
    const PX_PER_CM = 30;
    const marginX = 30;
    const offsetStart = randInt(0, 2); // segment commence à 0, 1 ou 2 cm sur la règle
    const totalCm = offsetStart + Math.ceil(longueurCm) + 2;

    const width = marginX * 2 + totalCm * PX_PER_CM;
    const segY = 35;        // segment AB (rapproché de la règle)
    const regleTop = 55;    // haut de la règle (≈ 15px sous le segment)
    const regleBot = 95;    // bas de la règle (hauteur 40px, englobe graduations + numéros)
    const height = regleBot + 10;

    const xA = marginX + offsetStart * PX_PER_CM;
    const xB = xA + longueurCm * PX_PER_CM;

    // Règle : rectangle fermé type bois naturel (jaune très clair)
    const regleRect =
        `<rect x="${marginX - 2}" y="${regleTop}" width="${totalCm * PX_PER_CM + 4}" height="${regleBot - regleTop}"`
        + ` fill="#fff9e6" stroke="#000" stroke-width="1.5" rx="2" />`;

    // Graduations (traits + numéros) — partent du haut de la règle
    let graduations = '';
    for (let i = 0; i <= totalCm; i++) {
        const x = marginX + i * PX_PER_CM;
        graduations += `<line x1="${x}" y1="${regleTop}" x2="${x}" y2="${regleTop + 10}" stroke="#000" stroke-width="1.5" />`;
        graduations += `<text x="${x}" y="${regleTop + 24}" text-anchor="middle" font-size="11" fill="#000">${i}</text>`;
        if (i < totalCm) {
            const xH = x + PX_PER_CM / 2;
            graduations += `<line x1="${xH}" y1="${regleTop}" x2="${xH}" y2="${regleTop + 5}" stroke="#000" stroke-width="1" />`;
        }
    }

    // Pointillés parallèles descendant des extrémités A et B du segment vers la règle
    const pointilles =
        `<line x1="${xA}" y1="${segY + 4}" x2="${xA}" y2="${regleTop - 1}" stroke="#666" stroke-width="1" stroke-dasharray="3 3" />`
        + `<line x1="${xB}" y1="${segY + 4}" x2="${xB}" y2="${regleTop - 1}" stroke="#666" stroke-width="1" stroke-dasharray="3 3" />`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">`
        + regleRect
        + graduations
        + pointilles
        // Segment AB : ligne épaisse + marqueurs d'extrémités
        + `<line x1="${xA}" y1="${segY}" x2="${xB}" y2="${segY}" stroke="#000" stroke-width="2.5" />`
        + `<line x1="${xA}" y1="${segY - 4}" x2="${xA}" y2="${segY + 4}" stroke="#000" stroke-width="2" />`
        + `<line x1="${xB}" y1="${segY - 4}" x2="${xB}" y2="${segY + 4}" stroke="#000" stroke-width="2" />`
        + `<text x="${xA - 5}" y="${segY - 8}" text-anchor="end" font-size="14" fill="#000">${A}</text>`
        + `<text x="${xB + 5}" y="${segY - 8}" font-size="14" fill="#000">${B}</text>`
        + `</svg>`;
};

const genMesurerRegle = () => {
    const [A, B] = pickN(2);
    // Longueurs de 2,0 à 9,5 cm par pas de 0,5 cm (16 longueurs possibles)
    const longueurs = [];
    for (let cm = 2; cm <= 9.5 + 0.0001; cm += 0.5) longueurs.push(Math.round(cm * 10) / 10);
    const longueurCm = randomChoice(longueurs);

    const svg = buildRegleSvg(A, B, longueurCm);
    const bonneReponse = fmt(longueurCm);

    // Énoncé réduit à la question : la figure montre déjà le segment sur la règle,
    // et la comparaison accepte indifféremment la virgule et le point décimal
    // (normalizeAnswer), donc prescrire la virgule n'apporte rien.
    const texte = `{{element:0}}<p>Quelle est la distance ${A}${B} en cm ?</p>`;

    const payload = makeSaisie(bonneReponse, texte, {
        ...opts(L_MESURER),
        suffixe: ' cm',
        elements_additionnels: {
            version: 1,
            elements: [imageElement(svg, `segment_${A}${B}_regle.svg`)],
        },
    });
    payload._meta.generatorKey = 'mesurer_regle_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Comparer au compas (tableau colonnesLibres + SVG)
// ═══════════════════════════════════════════════════════════

/**
 * Construit la config d'un element_additionnel interactif compas.
 * L'élève peut cliquer sur 2 extrémités pour mesurer l'écartement (outil d'aide).
 */
const buildCompasInteractifConfig = (segments, pxPerCm) => ({
    viewBox: { w: 500, h: 220 },
    pxPerCm,
    segments: segments.map(s => ({
        p1: s.A,
        p2: s.B,
        x1: s.x1, y1: s.y1,
        x2: s.x2, y2: s.y2,
    })),
});

const genComparerCompas = () => {
    const PX_PER_CM = 22;
    const nbSegments = randInt(5, 6);
    const letters = pickN(nbSegments * 2);

    // Dispose les segments sur une grille 3x2 avec léger bruit.
    const cols = 3;
    const cellW = 500 / cols;
    const cellH = 100;

    const segments = [];
    for (let i = 0; i < nbSegments; i++) {
        const lenCm = randInt(2, 7);
        const angleDeg = randInt(-35, 35);
        const angle = (angleDeg * Math.PI) / 180;
        const col = i % cols;
        const row = Math.floor(i / cols);
        const cx = Math.round(cellW * col + cellW / 2 + randInt(-10, 10));
        const cy = Math.round(cellH * row + 55 + randInt(-10, 10));
        const halfPx = (lenCm * PX_PER_CM) / 2;
        segments.push({
            A: letters[2 * i],
            B: letters[2 * i + 1],
            length: lenCm,
            x1: Math.round(cx - halfPx * Math.cos(angle)),
            y1: Math.round(cy - halfPx * Math.sin(angle)),
            x2: Math.round(cx + halfPx * Math.cos(angle)),
            y2: Math.round(cy + halfPx * Math.sin(angle)),
        });
    }

    // Génère toutes les paires possibles puis en tire 4.
    const allPairs = [];
    for (let i = 0; i < segments.length; i++) {
        for (let j = i + 1; j < segments.length; j++) {
            allPairs.push([segments[i], segments[j]]);
        }
    }
    const nbComparisons = Math.min(4, allPairs.length);
    const chosen = randomChoiceMultiple(allPairs, nbComparisons);

    const colonnes = ['<', '=', '>'];
    const lignes = chosen.map(([s1, s2]) => `${s1.A}${s1.B} ? ${s2.A}${s2.B}`);
    const bonnesReponses = chosen.map(([s1, s2]) => {
        if (s1.length < s2.length) return 0;
        if (s1.length === s2.length) return 1;
        return 2;
    });

    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const compasConfig = buildCompasInteractifConfig(segments, PX_PER_CM);

    // Consigne réduite à la contrainte de méthode : la figure montre les segments
    // et les colonnes < = > disent déjà quoi choisir.
    const texte = `<p>Utilise le compas virtuel pour comparer la longueur des segments demandés.</p>{{element:0}}`;

    const payload = {
        type: 'qcm',
        texte,
        contenu: { choix: [], bonne_reponse: br },
        elements_additionnels: {
            version: 1,
            elements: [
                { type: 'compas_interactif', config: compasConfig },
                { type: 'table', config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses } },
            ],
        },
        niveau: '6eme',
        matiere: 'maths',
        attendu: ATTENDU_ID,
        _meta: { label: L_COMPARER, generatorKey: 'comparer_compas_tableau' },
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Périmètres au compas (QCM 3 choix + SVG)
// ═══════════════════════════════════════════════════════════

/**
 * Construit la config d'un compas interactif en mode "report_figure".
 * Les côtés des 2 polygones deviennent des segments cliquables.
 * Chaque point est mappé à son axe de report (f1 ou f2).
 */
const buildPerimetreCompasConfig = (poly1, poly2, nom1, nom2, pxPerCm) => {
    // Construit les segments (côtés) de chaque polygone
    const mkSegments = (poly, names, figureId) => {
        const segs = [];
        for (let i = 0; i < poly.verts.length; i++) {
            const [x1, y1] = poly.verts[i];
            const [x2, y2] = poly.verts[(i + 1) % poly.verts.length];
            segs.push({
                p1: `${names[i]}_${figureId}`,        // suffixe pour éviter collision entre figures
                p2: `${names[(i + 1) % names.length]}_${figureId}`,
                p1Label: names[i],                     // nom court affiché
                p2Label: names[(i + 1) % names.length],
                x1, y1, x2, y2,
            });
        }
        return segs;
    };

    // Position de label calculée vers l'extérieur du polygone (1 entrée par sommet, dédup)
    const mkPointLabels = (poly, names, figureId) => {
        return poly.verts.map(([x, y], i) => {
            const dx = x - poly.cx;
            const dy = y - poly.cy;
            const len = Math.hypot(dx, dy) || 1;
            return {
                name: `${names[i]}_${figureId}`,
                label: names[i],
                x, y,
                labelX: x + (dx / len) * 13,
                labelY: y + (dy / len) * 13 + 4, // +4 pour centrer verticalement le texte
            };
        });
    };

    const names1 = nom1.split('');
    const names2 = nom2.split('');
    const segments = [
        ...mkSegments(poly1, names1, 'f1'),
        ...mkSegments(poly2, names2, 'f2'),
    ];
    const pointLabels = [
        ...mkPointLabels(poly1, names1, 'f1'),
        ...mkPointLabels(poly2, names2, 'f2'),
    ];

    // Map chaque point (avec son suffixe) → axe de report
    const pointToAxis = {};
    names1.forEach(n => { pointToAxis[`${n}_f1`] = 'f1'; });
    names2.forEach(n => { pointToAxis[`${n}_f2`] = 'f2'; });

    // Place le label de figure SOUS la bbox du polygone pour éviter chevauchement avec les sommets
    const maxY1 = Math.max(...poly1.verts.map(v => v[1]));
    const maxY2 = Math.max(...poly2.verts.map(v => v[1]));

    return {
        viewBox: { w: 500, h: 340 },
        pxPerCm,
        segments,
        pointLabels,
        mode: 'report_figure',
        figureLabels: [
            { cx: poly1.cx, cy: maxY1 + 28, text: `Figure 1 (${nom1})` },
            { cx: poly2.cx, cy: maxY2 + 28, text: `Figure 2 (${nom2})` },
        ],
        reportAxes: [
            { id: 'f1', label: 'Figure 1', x1: 40, x2: 460, y: 240 },
            { id: 'f2', label: 'Figure 2', x1: 40, x2: 460, y: 300 },
        ],
        pointToAxis,
    };
};

/** Crée un polygone convexe avec `nv` sommets autour d'un centre (cx, cy). */
const makeConvexPoly = (cx, cy, nv, rMin, rMax) => {
    const verts = [];
    for (let i = 0; i < nv; i++) {
        const angle = (i * 2 * Math.PI) / nv + (randInt(-12, 12) * Math.PI) / 180;
        const r = randInt(rMin, rMax);
        verts.push([Math.round(cx + r * Math.cos(angle)), Math.round(cy + r * Math.sin(angle))]);
    }
    return { verts, cx, cy };
};

const polyPerimeterPx = (verts) => {
    let p = 0;
    for (let i = 0; i < verts.length; i++) {
        const [x1, y1] = verts[i];
        const [x2, y2] = verts[(i + 1) % verts.length];
        p += Math.hypot(x2 - x1, y2 - y1);
    }
    return p;
};

const genPerimetreCompas = () => {
    let poly1, poly2, P1, P2, ratio;
    let safety = 0;
    // On veut |P1 − P2| / max(P1,P2) ≥ 0,15 pour que la comparaison visuelle soit nette.
    //
    // ⚠ DETTE CONNUE (relevée en revue, laissée sur arbitrage) — cette garde a
    // deux effets de bord mesurés sur 5000 tirages :
    //   • « Les deux périmètres sont égaux » n'est JAMAIS la bonne réponse
    //     (distracteur mort : sur 3 choix, il en reste 2).
    //   • La figure la plus grande à l'œil a TOUJOURS le plus grand périmètre
    //     (0 contre-exemple) → l'exercice se réussit sans reporter au compas.
    // Les deux se corrigent ensemble : autoriser parfois deux périmètres
    // proches ou égaux, c'est-à-dire précisément les cas où le compas sert.
    do {
        const nv1 = randomChoice([3, 4]);
        const nv2 = randomChoice([3, 4]);
        poly1 = makeConvexPoly(130, 90, nv1, 30, 55);
        poly2 = makeConvexPoly(370, 90, nv2, 30, 55);
        P1 = polyPerimeterPx(poly1.verts);
        P2 = polyPerimeterPx(poly2.verts);
        ratio = Math.abs(P1 - P2) / Math.max(P1, P2);
        safety++;
    } while (ratio < 0.15 && safety < 50);

    // Un SEUL tirage réparti entre les deux figures : deux appels indépendants
    // rendaient la même lettre à deux points distincts (44,8 % des tirages), ce
    // qui est une notation fausse — deux points ne partagent pas un nom.
    // 8 lettres au maximum pour 22 disponibles, aucun risque d'épuisement.
    const nv1 = poly1.verts.length;
    const toutesLettres = pickN(nv1 + poly2.verts.length);
    const nom1 = toutesLettres.slice(0, nv1).join('');
    const nom2 = toutesLettres.slice(nv1).join('');

    // Conversion px → cm pour affichage des labels (rayons polygones 30-55 px → ~1,5-2,5 cm)
    const PX_PER_CM = 22;
    const compasConfig = buildPerimetreCompasConfig(poly1, poly2, nom1, nom2, PX_PER_CM);

    const choixUnique = [
        `Le périmètre de la Figure 1 est plus petit que celui de la Figure 2`,
        `Les deux périmètres sont égaux`,
        `Le périmètre de la Figure 1 est plus grand que celui de la Figure 2`,
    ];
    const correct = P1 < P2 ? choixUnique[0] : choixUnique[2]; // on a forcé ratio ≥ 15%, donc jamais "égaux"

    // « compas virtuel » : même outil, même mot que sur comparer_compas_tableau.
    // Les noms des figures ne sont pas repris — les labels « Figure 1 (MNP) »
    // sont déjà écrits sous chaque polygone.
    const texte = `<p>Reporte successivement la longueur de chaque côté de chaque figure sur la demi-droite correspondante avec le compas virtuel, puis compare les périmètres obtenus.</p>{{element:0}}`;

    const payload = makeQcm(choixUnique, correct, texte, {
        ...opts(L_PERIMETRE),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'compas_interactif', config: compasConfig }],
        },
    });
    payload._meta.generatorKey = 'perimetre_compas_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Distance selon la position du point (QCM 4 choix)
// ═══════════════════════════════════════════════════════════

const genPositionPoint = () => {
    const [E, F, G] = pickN(3);
    // EF : entier entre 4 et 10 cm
    const EF = randInt(4, 10);
    // 3 scénarios possibles
    const scenario = randomChoice(['surSegment', 'surProlongement', 'nonPrecise']);

    const mkChoice = (v) => `${G}${F} = ${fmt(v)} cm`;
    const IMPOSSIBLE = `Impossible à calculer sans information supplémentaire`;

    let enonce, correct, distractors;

    if (scenario === 'surSegment') {
        // G ∈ [EF], EG < EF, calculer GF = EF - EG
        // On évite EG = EF/2 (sinon GF = EG → doublon avec le piège "reprise").
        let EG;
        do { EG = randInt(1, EF - 1); } while (EG * 2 === EF);
        const GF = EF - EG;
        enonce = `<p>${E}${F} = ${EF} cm et ${G} ∈ [${E}${F}] avec ${E}${G} = ${EG} cm.</p><p>Que vaut la distance ${G}${F} ?</p>`;
        correct = mkChoice(GF);
        distractors = [
            mkChoice(EF + EG), // piège : addition
            mkChoice(EG),      // piège : reprise de EG
            IMPOSSIBLE,
        ];
    } else if (scenario === 'surProlongement') {
        // G ∈ [EF) avec EG > EF → GF = EG - EF (F entre E et G)
        //
        // ⚠ DETTE CONNUE (relevée en revue, laissée sur arbitrage) — dans ce
        // scénario, GF ∈ [1..5] alors que EG = EF + GF lui est toujours
        // supérieur et EF + EG davantage encore : la bonne réponse est
        // MÉCANIQUEMENT la plus petite valeur proposée, à 100 % des tirages.
        // Mesuré sur 5000 tirages : « cocher le plus petit nombre » suffit dans
        // 48,9 % des cas tous scénarios confondus (hasard = 25 %).
        // Correction : varier la question — demander tantôt GF, tantôt EG en
        // donnant EF et GF. Changer les distracteurs ne suffit pas.
        const EG = EF + randInt(1, 5);
        const GF = EG - EF;
        enonce = `<p>${E}${F} = ${EF} cm et ${G} appartient à la demi-droite [${E}${F}) avec ${E}${G} = ${EG} cm.</p><p>Que vaut la distance ${G}${F} ?</p>`;
        correct = mkChoice(GF);
        distractors = [
            mkChoice(EF + EG), // piège : addition
            mkChoice(EG),      // piège : reprise de EG
            IMPOSSIBLE,
        ];
    } else {
        // G juste donné avec EG, sans préciser sa position → impossible.
        // On évite EG = EF (minGF = 0 → remplacé par EG → doublon) et EG = EF/2 (minGF = EG).
        let EG;
        do { EG = randInt(1, EF + 3); } while (EG === EF || EG * 2 === EF);
        // « tel que » et non « vérifie » : la seconde tournure est de niveau lycée.
        // Pas de « sans autre précision » : c'était le seul énoncé des trois à
        // porter cette mention, donc la repérer suffisait à cocher « Impossible »
        // sans rien comprendre. C'est à l'élève de constater que la position manque.
        enonce = `<p>${E}${F} = ${EF} cm et ${G} est un point tel que ${E}${G} = ${EG} cm.</p><p>Que vaut la distance ${G}${F} ?</p>`;
        correct = IMPOSSIBLE;
        const minGF = Math.abs(EF - EG);
        const maxGF = EF + EG;
        // Les 2 valeurs extrêmes sont de "vraies" valeurs possibles mais on ne peut pas trancher
        distractors = [
            mkChoice(maxGF),
            mkChoice(minGF === 0 ? EG : minGF),
            mkChoice(EG),
        ];
    }

    // Filet de sécurité : dédup stricte des 4 choix, avec tirage de remplaçants
    // arbitraires si jamais une collision résiduelle se glissait.
    const unique = [];
    for (const c of [correct, ...distractors]) {
        if (!unique.includes(c)) unique.push(c);
    }
    let safety = 0;
    while (unique.length < 4 && safety < 20) {
        const extra = mkChoice(randInt(1, 25));
        if (!unique.includes(extra)) unique.push(extra);
        safety++;
    }

    const choix = shuffle(unique);
    const payload = makeQcm(choix, correct, enonce, opts(L_POSITION));
    payload._meta.generatorKey = 'position_point_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 6 — Alignement par inégalité triangulaire (QCM 4 choix)
// ═══════════════════════════════════════════════════════════

/**
 * Construit 3 longueurs (en cm) avec un scénario donné :
 *  - 'aligné' : génère a + b = c. Le point du milieu est celui opposé à c.
 *  - 'non'     : génère 3 longueurs ne satisfaisant AUCUNE égalité parmi les 3 permutations.
 */
const buildTriplet = () => {
    if (Math.random() < 0.65) {
        // Aligné : a + b = c (c est la plus grande distance = entre les 2 extrémités)
        const a = randInt(3, 8);
        const b = randInt(3, 8);
        const c = a + b;
        return { aligned: true, a, b, c };
    }
    // Non aligné : on exige l'inégalité triangulaire STRICTE dans les trois sens.
    //
    // `a + b > c` exclut mécaniquement `a + b === c` (le cas aligné), donc une
    // seule condition couvre les deux rejets. Il ne suffit PAS d'écarter les
    // égalités : sans le « > », on laisse passer des triplets comme
    // CU = 4 cm, CH = 3 cm, UH = 10 cm, qu'aucun triplet de points ne peut
    // réaliser — on ne va pas de U à H en 10 cm si le détour par C n'en fait
    // que 7. L'énoncé interrogerait alors des points inexistants.
    //
    // 407 triplets valides sur les 512 de [3..10]³ (79,5 %), soit 98 formes
    // distinctes à permutation près : la boucle converge en ~1,3 tentative.
    let a, b, c, tries = 0;
    do {
        a = randInt(3, 10);
        b = randInt(3, 10);
        c = randInt(3, 10);
        tries++;
    } while (
        !(a + b > c && a + c > b && b + c > a) && tries < 50
    );
    return { aligned: false, a, b, c };
};

const genAlignement = () => {
    const [P, Q, R] = pickN(3);
    const triplet = buildTriplet();

    // Conversion d'unités 1 fois sur 3
    const useConversion = Math.random() < 0.35;

    // Affectation :
    // Si aligné : on étiquette de sorte que le point milieu soit au centre (between)
    //   Soit M = point du milieu, X et Y les extrémités.
    //   Choix : aléatoire parmi {(P,Q,R), (Q,R,P), (R,P,Q)} pour varier quel point est au milieu.
    //   On définit : pointMilieu, pointA, pointB où A et B sont les extrémités.
    //   Distances : pointA-pointMilieu = a, pointMilieu-pointB = b, pointA-pointB = a + b.
    let pointMilieu, pointA, pointB;
    let d_AM, d_MB, d_AB;

    if (triplet.aligned) {
        [pointA, pointMilieu, pointB] = shuffle([P, Q, R]);
        d_AM = triplet.a;
        d_MB = triplet.b;
        d_AB = triplet.c;
    } else {
        // Étiquetage libre
        const shuffled = shuffle([P, Q, R]);
        pointA = shuffled[0]; pointMilieu = shuffled[1]; pointB = shuffled[2];
        // Distances attribuées arbitrairement (sans égalité)
        d_AM = triplet.a;
        d_MB = triplet.b;
        d_AB = triplet.c;
    }

    // Formate les mesures (en cm par défaut, avec conversion optionnelle sur une des 3)
    const mesuresBrutes = [
        { pair: `${pointA}${pointMilieu}`, value: d_AM, unit: 'cm' },
        { pair: `${pointMilieu}${pointB}`, value: d_MB, unit: 'cm' },
        { pair: `${pointA}${pointB}`,       value: d_AB, unit: 'cm' },
    ];

    if (useConversion) {
        const idx = randInt(0, 2);
        const v = mesuresBrutes[idx].value;
        // Tente conversion en dm (v/10 doit être propre) ou en mm
        if (v % 10 === 0) {
            mesuresBrutes[idx].value = v / 10;
            mesuresBrutes[idx].unit = 'dm';
        } else if (v >= 5) {
            // exprimer en dm décimal
            mesuresBrutes[idx].value = fmt(v / 10);
            mesuresBrutes[idx].unit = 'dm';
        } else {
            mesuresBrutes[idx].value = v * 10;
            mesuresBrutes[idx].unit = 'mm';
        }
    }

    const mesuresStr = mesuresBrutes
        .map(m => `${m.pair} = ${m.value} ${m.unit}`)
        .join(', ');

    // Construction des choix
    const choixSet = new Set();
    choixSet.add('Non alignés');
    // 3 ordres : point milieu différent à chaque fois
    // Ordre correct si aligné = {pointA, pointMilieu, pointB}
    const formatOrdre = (x, m, y) => `Alignés dans l'ordre ${x}, ${m}, ${y}`;
    choixSet.add(formatOrdre(pointA, pointMilieu, pointB));
    choixSet.add(formatOrdre(pointA, pointB, pointMilieu));
    choixSet.add(formatOrdre(pointMilieu, pointA, pointB));

    const choix = [...choixSet];
    const correct = triplet.aligned
        ? formatOrdre(pointA, pointMilieu, pointB)
        : 'Non alignés';

    const texte = `<p>On donne les mesures suivantes :</p><p>${mesuresStr}.</p><p>Les points ${P}, ${Q}, ${R} sont-ils alignés ? Si oui, dans quel ordre ?</p>`;

    const payload = makeQcm(choix, correct, texte, opts(L_ALIGNEMENT));
    payload._meta.generatorKey = 'alignement_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    notation_vf_tableau:         genNotationVF,
    mesurer_regle_saisie:        genMesurerRegle,
    comparer_compas_tableau:     genComparerCompas,
    perimetre_compas_qcm_unique: genPerimetreCompas,
    position_point_qcm_unique:   genPositionPoint,
    alignement_qcm_unique:       genAlignement,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', qcm_images: 'QCM images',
    saisie: 'Saisie', texte: 'Saisie',
    tableau_vf: 'Vrai/Faux', vf_tableau: 'Vrai/Faux', vf_figure: 'Vrai/Faux', tableau: 'Vrai/Faux',
    programme: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'notation',         label: L_NOTATION,   formatsList: buildFormats('notation') },
    { id: 'mesurer_regle',    label: L_MESURER,    formatsList: buildFormats('mesurer_regle') },
    { id: 'comparer_compas',  label: L_COMPARER,   formatsList: buildFormats('comparer_compas') },
    { id: 'perimetre_compas', label: L_PERIMETRE,  formatsList: buildFormats('perimetre_compas') },
    { id: 'position_point',   label: L_POSITION,   formatsList: buildFormats('position_point') },
    { id: 'alignement',       label: L_ALIGNEMENT, formatsList: buildFormats('alignement') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genNotationVF,
    genMesurerRegle,
    genComparerCompas,
    genPerimetreCompas,
    genPositionPoint,
    genAlignement,
};

   